import React, { useCallback, useEffect, useMemo } from 'react';
import { useLazyGetAllusersQuery } from '../../features/apis/authApi';
import useCurrentUser from '../../hooks/useCurrentUser';
import lazyWithRetry from '../../lazyWithRetry';
import importRetry from '../../importRetry';
import { Suspense, useState } from 'react';
import LeaveEdit from '../../components/LeaveManagement/LeaveEdit';
import Activation from '../../components/Activation/Activation';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  useDisclosure,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Modal,
  ModalHeader,
} from '@chakra-ui/react';
import { Button, Input, InputGroup, InputRightElement } from '@chakra-ui/react';
import EmployeeCard from '../../components/EmployeeCard/EmployeeCard';
import CardSkeleton from '../../components/Skeletons/CardSkeleton';
import { Doc, DocUrl } from '../../util/commonType';
import useSectionPermission from '../../hooks/useSectionPermission';
import PageLayout from '../../layouts/PageLayout';
import { FiFilter, FiUsers, FiCheck, FiUserPlus, FiEdit } from 'react-icons/fi';
import { SearchIcon } from '@chakra-ui/icons';
import DeleteEmployee from '../../components/DeleteEmployee/DeleteEmployee';
import EmployeeAccessControl from '../../components/EmployeeCard/EmployeeAccessControl';
const FILTERS = [
  'All',
  'Intern',
  'Probation',
  'Pending',
  'Verified',
  'Active',
] as const;

const InviteEmployee = lazyWithRetry(() =>
  importRetry(() => import('../../components/InviteEmployee/InviteEmployee'))
);
const Userdocs = lazyWithRetry(() =>
  importRetry(() => import('../../components/EmployeeCard/Userdocs'))
);
const VerifyUser = lazyWithRetry(() =>
  importRetry(() => import('../../components/EmployeeCard/VerifyUser'))
);

const Employees = () => {
  const [triggerGetAllUsers, { data, isLoading, isFetching, isSuccess }] =
    useLazyGetAllusersQuery();

  const [adminOption, setAdminOption] = useState<string>('');
  const [modalState, setModalState] = useState<boolean>(false);
  const [docModalState, setDocModalState] = useState<boolean>(false);
  const [profileUser, setProfileUser] = useState<any>();
  const setAdminOptionOptimised = useCallback(() => setAdminOption, []);
  const setModalStateOptimised = useCallback(() => setModalState, []);
  const setProfileUserOptimised = useCallback(() => setProfileUser, []);

  const currentUser = useCurrentUser();
  const { isOpen, onOpen, onClose } = useDisclosure();
  useEffect(() => {
    triggerGetAllUsers();
  }, [triggerGetAllUsers]);

  const [searchterm, setSearchTerm] = useState<string>('');
  const [filteritem, setFilteritem] = useState<(typeof FILTERS)[number]>('All');
  const [selectedOption, setSelectedOption] = useState<string>('');
  const [docUrl, setDocUrl] = useState<DocUrl>({
    fileType: '',
    fileUrl: '',
    doc: null,
    userId: '',
  });
  const { create } = useSectionPermission('employees');
  const openDocModal = (
    url: string,
    type: string,
    doc: Doc,
    userId: string
  ) => {
    setDocUrl({
      fileType: type,
      fileUrl: url,
      doc,
      userId,
    });
    setDocModalState(true);
  };
  const closeDocModal = () => {
    setDocModalState(false);
  };
  const handleSelect = (item: (typeof FILTERS)[number]) => {
    setFilteritem(item);
    onClose();
  };

  const employeeFilter = useMemo(
    () =>
      data?.employee.filter((val) => {
        if (filteritem === 'All') {
          return val;
        } else if (
          filteritem?.toLowerCase() === 'verified' &&
          val.verified === 'true'
        ) {
          return val;
        } else if (
          filteritem?.toLowerCase() === 'intern' &&
          val?.employment_type === filteritem
        ) {
          return val;
        } else if (
          filteritem?.toLowerCase() === 'probation' &&
          val?.isProbationServed === false
        ) {
          return val;
        } else if (
          filteritem?.toLowerCase() === 'active' &&
          val?.isActive === true &&
          val?.verified === 'true'
        ) {
          return val;
        } else if (
          filteritem?.toLowerCase() === 'pending' &&
          val?.verified === 'false'
        ) {
          return val;
        }
      }),
    [data?.employee, filteritem]
  );

  const filteredList = useMemo(() => {
    const list = (filteritem === 'All' ? data?.employee : employeeFilter) || [];
    if (searchterm.trim()) {
      return list.filter((each) =>
        each.name?.toLowerCase().includes(searchterm.toLowerCase())
      );
    }
    return list;
  }, [data?.employee, employeeFilter, filteritem, searchterm]);

  if (!data || isLoading || isFetching) {
    return (
      <PageLayout pageTitle="Employees">
        <div className="w-full px-6 mt-6 space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className=" shadow-lg rounded-md px-4 py-3 bg-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-[70px] h-[70px] rounded-full bg-gray-200 animate-pulse shrink-0" />
                  <div className="space-y-2">
                    <div className="h-4 w-48 bg-gray-200 rounded animate-pulse" />
                    <div className="h-3 w-32 bg-gray-200 rounded animate-pulse" />
                  </div>
                </div>
                <div className="h-8 w-8 bg-gray-200 rounded animate-pulse" />
              </div>
            </div>
          ))}
        </div>
      </PageLayout>
    );
  } else if (data && isSuccess) {
    return (
      <PageLayout
        pageTitle="Employees"
        action2={
          <div className="flex gap-4 items-center">
            {create ? (
              <Button
                onClick={() => {
                  setAdminOption('add_user');
                  setModalState(true);
                }}
                borderRadius={'5px'}
                bgGradient="linear(to-r, #6535EB, #9F3198)"
                color={'whiteAlpha.900'}
                py={0.5}
                px={5}
                _hover={{ backgroundColor: '#5930c7' }}
              >
                <span className="text-xs">+ Add New </span>
              </Button>
            ) : null}

            <Popover
              isOpen={isOpen}
              onOpen={onOpen}
              onClose={onClose}
              placement="bottom-end"
            >
              <PopoverTrigger>
                <Button
                  ml={4}
                  gap={2}
                  bg="#FFFFFF"
                  border="0.5px solid #C6C6C6"
                  display={{ base: 'none', md: 'flex' }}
                >
                  <FiFilter color="#6C6C6C" />
                  <span className="text-[#6C6C6C]">Filters</span>
                </Button>
              </PopoverTrigger>

              <PopoverContent style={{ width: '200px' }}>
                <PopoverBody className="w-full">
                  {FILTERS.map((item, index) => (
                    <button
                      onClick={() => handleSelect(item)}
                      className="w-full !text-left text-xs text-gray-600  hover:bg-gray-100 p-2 font-semibold hover:rounded-md"
                      key={index}
                    >
                      {item}
                    </button>
                  ))}
                </PopoverBody>
              </PopoverContent>
            </Popover>
          </div>
        }
        action1={
          <div className="items-center gap-6 hidden md:flex">
            <InputGroup width={'lg'}>
              <InputRightElement>
                <SearchIcon color="#C6C6C6" boxSize={3} />
              </InputRightElement>

              <Input
                py={0}
                px={4}
                type="text"
                placeholder="Search Employee..."
                value={searchterm}
                onChange={(e) => setSearchTerm(e.target.value)}
                _placeholder={{
                  color: '#C6C6C6',
                  fontWeight: 'light',
                  fontSize: 'xs',
                }}
                border="0.5px solid #C6C6C6"
                borderRadius="md"
                _focus={{ borderColor: '#C6C6C6', boxShadow: 'none' }}
              />
            </InputGroup>
          </div>
        }
      >
        <div className="flex items-center justify-between flex-col mt-6   mx-6 h-[70vh]">
          <div className={'w-full mt-8'}>
            {filteredList?.map((d, index) => {
              return (
                <EmployeeCard
                  key={d?._id + index}
                  user_id={d?._id}
                  name={d?.name}
                  email={d?.email}
                  userRole={d?.role}
                  designation={d?.designation}
                  currentUserRole={currentUser?.role}
                  currentUserId={currentUser?._id}
                  verified={d?.verified}
                  setAdminOption={setAdminOptionOptimised()}
                  setProfileUser={setProfileUserOptimised()}
                  setModalState={setModalStateOptimised()}
                  modalState={modalState}
                  assigned_HR={d?.assigned_HR}
                  assigned_Manager={d?.assigned_Manager}
                  documents={d?.docs}
                  dob={d?.dob}
                  permanentAddress={d?.permanent_address || d?.address}
                  presentAddress={d?.present_address || d?.address}
                  mobile={d?.mobile}
                  emergency_contact={d?.emergency_contact}
                  leave_data={d?.leave_data}
                  filterBy={filteritem}
                  setSelectedOption={setSelectedOption}
                  selectedOption={selectedOption}
                  isActive={d?.isActive}
                  avatar={d?.avatar}
                />
              );
            })}
          </div>
           {modalState && adminOption !== '' ? (
             <Modal
               isOpen={modalState && adminOption !== ''}
               onClose={() => {
                 setModalState(false);
                 setAdminOption('');
               }}
               size="xl"
               isCentered
             >
               <ModalOverlay bg="blackAlpha.200" backdropFilter="blur(4px)" />{' '}
               <ModalContent
                 maxH="90vh"
                 overflowY="auto"
                 borderWidth="0.5px"
                 borderColor="#D4D4D4"
                 mx={4}
               >
                 <ModalHeader
                   fontSize="sm"
                   color="whiteAlpha.900"
                   background="purple.500"
                   borderTopRadius="md"
                   py={3}
                   px={4}
                 >
                   <div className="flex items-center gap-2">
                     {adminOption === 'change_user_details' && (
                       <>
                         <FiUsers size={16} />
                         <span>Update Employee Access</span>
                       </>
                     )}
                     {adminOption === 'verify_user' && (
                       <>
                         <FiCheck size={16} />
                         <span>Verify User</span>
                       </>
                     )}
                     {adminOption === 'add_user' && (
                       <>
                         <FiUserPlus size={16} />
                         <span>Invite Employee</span>
                       </>
                     )}
                     {adminOption === 'leave_edit' && (
                       <>
                         <FiEdit size={16} />
                         <span>Edit Leave</span>
                       </>
                     )}
                   </div>
                 </ModalHeader>
                 <ModalCloseButton
                   color="whiteAlpha.900"
                   my={1}
                   zIndex={2}
                   sx={{
                     _hover: {
                       bg: 'transparent',
                       color: 'white',
                     },
                   }}
                 />
                 <ModalBody pb={6}>
                    {adminOption === 'change_user_details' && (
                      <EmployeeAccessControl
                        profile_user={profileUser}
                        current_user={currentUser}
                        refetch={triggerGetAllUsers}
                        isOpen={modalState && adminOption === 'change_user_details'}
                        onClose={() => {
                          setModalState(false);
                          setAdminOption('');
                        }}
                      />
                    )}

                    {adminOption === 'verify_user' && (
                      <Suspense
                        fallback={
                          <div className="flex justify-center items-center py-10">
                            <CardSkeleton lines={3} />
                          </div>
                        }
                      >
                       <VerifyUser
                         profile_user={profileUser}
                         onClose={() => {
                           setModalState(false);
                           setAdminOption('');
                         }}
                         refetch={triggerGetAllUsers}
                       />
                     </Suspense>
                   )}

                    {adminOption === 'add_user' && (
                      <Suspense
                        fallback={
                          <div className="flex justify-center items-center py-10">
                            <CardSkeleton lines={3} />
                          </div>
                        }
                      >
                       <InviteEmployee
                         current_user_id={currentUser?._id}
                         refetch={triggerGetAllUsers}
                         onClose={() => {
                           setModalState(false);
                           setAdminOption('');
                         }}
                       />
                     </Suspense>
                   )}
                    {adminOption === 'leave_edit' && (
                      <Suspense
                        fallback={
                          <div className="flex justify-center items-center py-10">
                            <CardSkeleton lines={3} />
                          </div>
                        }
                      >
                       <LeaveEdit
                         profileUser={profileUser}
                         current_user={currentUser}
                         onClose={() => {
                           setModalState(false);
                           setAdminOption('');
                         }}
                       />
                     </Suspense>
                   )}
                 </ModalBody>
               </ModalContent>
             </Modal>
           ) : null}
           {adminOption === 'details' && (
             <Suspense fallback={
               <div className="flex justify-center items-center py-10">
                 <CardSkeleton showImage lines={2} />
               </div>
             }>
              <Userdocs
                isOpen={adminOption === 'details'}
                onClose={() => {
                  setModalState(false);
                  setAdminOption('');
                }}
                profile_user={profileUser}
                openDocModal={openDocModal}
                docModalState={docModalState}
                documents={profileUser.documents}
                docUrl={docUrl}
                onDocClose={closeDocModal}
              />
            </Suspense>
          )}
          <Activation
            profileUser={profileUser}
            isOpened={adminOption === 'update_option'}
            refetch={triggerGetAllUsers}
            onClose={() => {
              setModalState(false);
              setSelectedOption('');
              setAdminOption('');
              setProfileUser(undefined);
            }}
          />
          <DeleteEmployee
            profileUser={profileUser}
            isOpened={adminOption === 'delete_option'}
            refetch={triggerGetAllUsers}
            onClose={() => {
              setModalState(false);
              setSelectedOption('');
              setAdminOption('');
              setProfileUser(undefined);
            }}
          />
        </div>
      </PageLayout>
    );
  } else {
    return <span>No Data Found!!!</span>;
  }
};

export default React.memo(Employees);
